import React from 'react';
import Paper from '@material-ui/core/Paper';
import Typography from '@material-ui/core/Typography';
import { makeStyles } from '@material-ui/core/styles';
import { useTranslation } from 'react-i18next';

import { multiIOMapping } from '../Constants/constants';
import { getMultiIOOverwriteLabel } from '../Utils/info_utils';
import { supportsMultiIoOverrides } from '../Utils/multi_io_settings';
import {
  getMultiIOCurrentLabel,
  getMultiIOFunctionIndexLabel,
  getMultiIOHardwareTypeLabel,
  getMultiIOStateValue
} from './MultiOutLabel';

const useStyles = makeStyles((theme) => ({
  card: {
    boxSizing: 'border-box',
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
    minWidth: 0,
    minHeight: 140,
    padding: theme.spacing(2),
    backgroundColor: theme.palette.innerContainer?.background ?? theme.palette.background.paper,
    color: theme.palette.fontcolor ?? theme.palette.text.primary,
    boxShadow: `0 3px 2px ${theme.palette.innerContainer?.shadow ?? 'rgba(0, 0, 0, 0.25)'}`
  },
  primary: {
    display: 'grid',
    gridTemplateColumns: 'minmax(0, 1fr) auto',
    alignItems: 'start',
    columnGap: theme.spacing(2)
  },
  functionName: {
    ...theme.typography.body1,
    minWidth: 0,
    overflowWrap: 'anywhere',
    lineHeight: 1.35
  },
  state: {
    color: theme.palette.primary.main,
    fontSize: '1.3rem',
    fontWeight: theme.typography.fontWeightMedium,
    lineHeight: 1.25,
    whiteSpace: 'nowrap'
  },
  unavailableState: {
    color: theme.palette.text.secondary
  },
  memo: {
    marginTop: theme.spacing(1),
    color: theme.palette.text.secondary,
    overflowWrap: 'anywhere'
  },
  current: {
    marginTop: theme.spacing(1),
    color: theme.palette.primary.main
  },
  metadata: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(0.75),
    marginTop: 'auto',
    paddingTop: theme.spacing(1.5),
    color: theme.palette.text.secondary,
    fontSize: theme.typography.body2.fontSize
  }
}));

export default function MultiIOInfoCard({
  controller,
  zone,
  index,
  memo,
  func,
  funcIndex,
  value,
  type,
  info,
  infoType,
  monitor,
  overwriteSelect,
  overwriteValue
}) {
  const classes = useStyles();
  const { t } = useTranslation();
  const functionName = t(multiIOMapping[func] ?? multiIOMapping['default']);
  const functionIndex = getMultiIOFunctionIndexLabel(controller, func, funcIndex, t, zone);
  const state = getMultiIOStateValue(controller, func, value, type, t).trim();
  const hardwareType = getMultiIOHardwareTypeLabel(controller, type, t);
  const current = getMultiIOCurrentLabel(info, infoType, t, monitor);
  const hasMemo = memo != null && memo !== -1 && String(memo).trim() !== '';
  const stateUnavailable = !state || state.includes('---');
  const overwriteLabel = supportsMultiIoOverrides(controller)
    ? getMultiIOOverwriteLabel(overwriteSelect, overwriteValue, type, t) : null;

  return (
    <Paper className={classes.card} data-testid="multi-io-info-card">
      <div className={classes.primary}>
        <Typography className={classes.functionName} data-testid="multi-io-card-function">
          {functionName}
          {functionIndex !== null ? ` [${functionIndex}]` : ''}
        </Typography>
        <Typography
          className={`${classes.state} ${stateUnavailable ? classes.unavailableState : ''}`}
          data-testid="multi-io-card-state"
        >
          {state || '---'}
        </Typography>
      </div>

      {overwriteLabel && (
        <Typography variant="body2" color="primary" data-testid="multi-io-overwrite-status">
          {overwriteLabel}
        </Typography>
      )}

      {hasMemo && (
        <Typography className={classes.memo} data-testid="multi-io-card-memo">
          {memo}
        </Typography>
      )}

      {current && <Typography className={classes.current}>{current}</Typography>}

      <Typography component="div" className={classes.metadata}>
        <span>{`I/O ${index}`}</span>
        {hardwareType && (
          <>
            <span aria-hidden="true">·</span>
            <span>{hardwareType}</span>
          </>
        )}
      </Typography>
    </Paper>
  );
}
